import * as _ from "lodash";
import * as React from "react";
import * as S from "@styled";
import { connect } from "react-redux";
import Select, { ParameterInputLanguageModeSelector } from "shared/components/select/Select";

import { DispatchType, printWarning, Utilities } from "../../shared/utilities";
import { changeParameterInputMode } from "../actions/ChangeParameterInputMode";
import { toggleWordWrap, updateParameter } from "../actions/UpdateParameter";
import { getInitialMode } from "../model/DefaultParametersInitializer";
import { IParameterSchema } from "../model/IProcessData";
import { ParameterEditorValue } from "../model/ParameterEditorValue";
import { State } from "../model/State";
import { ValidationError } from "../model/ValidationData";

import LabelWithTooltip from "shared/components/LabelWithTooltip";
import { ParameterSecretIndicator } from "./ParameterSecretIndicator";
import { ValidationErrorDisplay } from "./ValidationErrorDisplay";


import EnvironmentVariableExpander from "./EnvironmentVariableExpander";
import { HashMap } from "../../shared/model/HashMap";

import MonacoEditor from "../../shared/monaco/components/MonacoEditor";
import { Option } from "shared/model/Option";
import CustomToggle from "shared/components/CustomToggle";

export const ParameterInputModeSelector: React.FC<ParameterInputModeSelectorProps> = (props: ParameterInputModeSelectorProps) => {

    const menuItems: Option[] = [];
    if (!!EDITOR_MODE_NAMES[props.currentModeName] || (props.originalModeName === ParameterEditorMode[ParameterEditorMode.handlebars] || props.originalModeName === ParameterEditorMode[ParameterEditorMode.text])) {
        // add all menu options as well

        Object.keys(EDITOR_MODE_NAMES).filter((key) => key !== props.currentModeName).map((modeKey) =>
            menuItems.push({
                label: EDITOR_MODE_NAMES[modeKey] as string,
                value: modeKey
            })
        );

        if (props.currentModeName !== ParameterEditorMode[ParameterEditorMode.csharp]) {
            menuItems.push({
                value: "csharp",
                label: FRENDS.Localizations.Expression
            });
        }
        menuItems.push({
            label: props.disableWordWrap ? FRENDS.Localizations.EnableWordWrap : FRENDS.Localizations.DisableWordWrap,
            value: "toggleWordWrap"
        });
    } else {
        if (props.originalModeName !== props.currentModeName) {
            menuItems.push({
                label: OTHER_MODE_NAMES[props.originalModeName] as string,
                value: props.originalModeName
            });
        }
        if (props.currentModeName !== ParameterEditorMode[ParameterEditorMode.csharp]) {
            menuItems.push({
                label: FRENDS.Localizations.Expression,
                value: "csharp"
            });
        }
    }

    const handleEditorModeOrToggleWordWrapChange = (option: Option) => {
        if (option.value === "toggleWordWrap") {
            if (props.onToggleWordWrap) {
                props.onToggleWordWrap();
            }
        } else {
            if (props.handleEditorModeChange) {
                props.handleEditorModeChange(option.value);
            }
        }

    };

    const defaultOption: Option = {
        label: EDITOR_MODE_NAMES[props.currentModeName] || (OTHER_MODE_NAMES[props.currentModeName]) as string,
        value: EDITOR_MODE_NAMES[props.currentModeName] || (OTHER_MODE_NAMES[props.currentModeName])
    }

    return (
        <ParameterInputLanguageModeSelector
            id={props.inputPropertyName + "_editor_settings"}
            classNamePrefix="react-select"
            className="language-mode-selector"
            value={Utilities.valueFromId(menuItems, EDITOR_MODE_NAMES[props.currentModeName]?.toLowerCase()) || defaultOption}
            options={menuItems}
            onChange={handleEditorModeOrToggleWordWrapChange}
            isDisabled={props.disabled}
            width={"115px"}
        />
    );
};

interface ParameterInputModeSelectorProps {
    inputPropertyName: string | undefined;
    currentModeName: string;
    disableWordWrap?: boolean;
    originalModeName: string;
    handleEditorModeChange: ((value: string) => void) | undefined;
    onToggleWordWrap?: () => void;
    disabled?: boolean;
}

// tslint:disable-next-line:max-classes-per-file
class ParameterInputElement extends React.Component<ParameterInputElementProps, { isEditorInitialized?: boolean }> {

    constructor(props: ParameterInputElementProps) {
        super(props);

        this.state = {};
    }

    public shouldComponentUpdate(nextProps: ParameterInputElementProps, nextState: { isEditorInitialized?: boolean }) {
        return !_.isEqual(this.props, nextProps) || !_.isEqual(this.state, nextState);
    }

    public isValid(newValue: string | string[], requiredPattern?: string): boolean {
        if (requiredPattern) {
            return new RegExp(requiredPattern).test(newValue as string);
        }
        return !Array.isArray(newValue) && !!newValue;
    }

    public render() {
        let mode = (ParameterEditorMode as unknown as HashMap<ParameterEditorMode>)[this.props.value.mode];
        if (!mode) {
            this.props.onWarning(`Unknown mode ${this.props.value.mode}. Will default to csharp`);
            mode = ParameterEditorMode.csharp;
        }
        let valueToUse = this.props.value && this.props.value.value;

        if (mode === ParameterEditorMode.toggle) {
            const switchState = this.props.value.value as boolean;
            return (
                <CustomToggle
                    checked={switchState}
                    onChange={this.handleToggleChange}
                    disabled={this.props.disabled}
                />
            );
        }

        if (mode === ParameterEditorMode.select) {
            const options: Option[] | null | undefined = this.props.parameterSchema && this.props.parameterSchema.subParameters
                && this.props.parameterSchema.subParameters
                    .filter((item) => !item.isHidden)
                    .map((item) => ({
                        value: item.name,
                        label: item.label ?? item.name,
                    }));
            if (options !== null && options !== undefined) {
                return (
                    <Select
                        value={Utilities.valueFromId(options, this.props.value.value.toString())}
                        options={options}
                        onChange={this.handleSelectChange}
                        isDisabled={this.props.disabled}
                    />
                );
            }
        }

        if (mode === ParameterEditorMode.integer) {
            const intValue = parseInt(valueToUse as string, 10);
            if (isNaN(intValue)) {
                this.props.onWarning(`Invalid number value ${valueToUse} for number input. Will default to '0'`);
                valueToUse = "0";
            } else {
                valueToUse = intValue.toString();
            }
            return <S.Input
                isFullWidth={true}
                name={this.props.name}
                key={this.props.name}
                type="number"
                onChange={this.handleInputChange}
                defaultValue={valueToUse.toString()}
                min={this.props.min}
                max={this.props.max}
                disabled={this.props.disabled}
            />;
        }

        if ((mode === ParameterEditorMode.handlebars || mode === ParameterEditorMode.text) && (this.props.parameterSchema?.isSecret || this.props.parameterSchema?.inputType === "password")) {
            if (_.isUndefined(valueToUse) || _.isNull(valueToUse)) {
                this.props.onWarning(`Invalid string ${valueToUse} for password input. Will default to empty string`);
                valueToUse = "";
            }
            return this.props.isUnmasked ? (
                <S.ExpressionEditorContainer style={{ display: 'flex' }}>
                    <MonacoEditor
                        onChange={this.handleEditorChange}
                        value={valueToUse.toString()}
                        mode={mode}
                        wordWrap={!this.props.value.disableWordWrap}
                        key={this.props.name + "_editor"}
                        readOnly={this.props.disabled}
                    />
                    <button onClick={this.props.onUnmask}>
                        <i className="ci-show" />
                    </button>
                </S.ExpressionEditorContainer>
            ) : (
                <S.PasswordInput
                    disabled={this.props.disabled}
                    isFullWidth={true}
                    name={this.props.name}
                    key={this.props.name}
                    autoComplete="new-password"
                    onChange={this.handleInputChange}
                    value={valueToUse.toString()}
                    onUnmask={this.props.onUnmask}
                />
            );
        }

        if (_.isUndefined(valueToUse) || _.isNull(valueToUse)) {
            this.props.onWarning(`Invalid string ${valueToUse} for text input. Will default to empty string`);
            valueToUse = "";
        }

        return (
            <S.ExpressionEditorContainer>
                <MonacoEditor
                    mode={mode}
                    key={this.props.name + "_editor"}
                    wordWrap={!this.props.value.disableWordWrap}
                    onChange={this.handleEditorChange}
                    value={valueToUse.toString()}
                    readOnly={this.props.disabled}
                />
            </S.ExpressionEditorContainer>
        );
    }

    private handleEditorChange = (newValue: string | undefined) => {
        if (_.isEqual(newValue, this.props.value.value)) {
            // no actual change
            return;
        }
        if (newValue !== undefined) {
            this.props.onChange({
                mode: this.props.value.mode,
                value: newValue
            }, this.isValid(newValue, this.props.requiredPattern));
        }
    }

    private handleToggleChange = (checked: boolean, event: any, id: string) => {
        if (this.props.value.value !== checked) {
            this.props.onChange({
                mode: this.props.value.mode,
                value: checked
            }, true);
        }
    }

    private handleInputChange = (input: React.FormEvent<any>) => {
        const newValue = (input.target as HTMLInputElement).value;
        // Input.target.value is already a string even if it is a number input
        if (this.props.value.value !== newValue) {
            this.props.onChange({
                mode: this.props.value.mode,
                value: newValue
            }, true);
        }
    }

    private handleSelectChange = (option: Option) => {
        this.props.onChange({
            mode: this.props.value.mode,
            value: option.value
        }, true);
    }
}

// tslint:disable-next-line:max-classes-per-file
export class ParameterInput extends React.Component<ParameterInputProps, ParameterInputState> {
    constructor(props: ParameterInputProps) {
        super(props);
        this.state = {
            isUnmasked: false
        };
    }
    public render() {
        const parameterLabel = this.props.parameterSchema ?
            <LabelWithTooltip
                label={this.props.label}
                defaultValue={this.props.parameterSchema.defaultValue}
                description={this.props.parameterSchema.description}
                type={this.props.parameterSchema.type}
                shouldNotDisplayIcon={!this.state.isHovering}
                useHover
            />
            : Utilities.humanizeLabel(this.props.label);
        const modeSelector = !this.props.isForCompletion && this.state.isHovering && this.props.parameterSchema
            ? <ParameterInputModeSelector
                handleEditorModeChange={this.props.onChangeParameterInputMode}
                onToggleWordWrap={this.props.onToggleWordWrap}
                inputPropertyName={this.props.name}
                currentModeName={this.props.value.mode}
                disableWordWrap={this.props.value.disableWordWrap}
                originalModeName={getInitialMode(this.props.parameterSchema.parameterType, this.props.parameterSchema.inputType)}
                disabled={this.props.disabled}
            />
            : null;

        return (
            <S.FormGroup
                onMouseEnter={this.handleMouseEnter}
                onMouseLeave={this.handleMouseLeave}
                variant={this.props.errors.length > 0 ? S.FormGroupVariant.error : S.FormGroupVariant.primary}
            >
                <S.FlexContainer justifySpaceBetween={true} noWrap={true} gap={14}>
                    <S.Label className="alert-label">
                        <S.FlexContainer centerItems={true}>
                            {parameterLabel}
                            <S.Label>
                                <EnvironmentVariableExpander key={this.props.value.value.toString()} shouldDisplayIcon={this.state.isHovering ?? false} parameterInputValue={this.props.value.value} />
                            </S.Label>
                        </S.FlexContainer>
                    </S.Label>
                    <S.Label>
                        {modeSelector}
                    </S.Label>
                </S.FlexContainer>
                <ParameterInputElement
                    elementId={this.props.elementId}
                    key={this.props.key}
                    value={this.props.value}
                    name={this.props.name}
                    label={this.props.label}
                    onChange={this.props.onChange}
                    parameterSchema={this.props.parameterSchema}
                    requiredPattern={this.props.requiredPattern}
                    min={this.props.min}
                    max={this.props.max}
                    onWarning={this.handleWarning}
                    disabled={this.props.disabled}
                    panelWidth={this.props.panelWidth}
                    isUnmasked={this.state.isUnmasked}
                    onUnmask={this.handleUnmasking} 
                />
                <ValidationErrorDisplay errors={this.props.errors} />
            </S.FormGroup>
        );
    }

    private handleUnmasking = () => {
        this.setState({ isUnmasked: !this.state.isUnmasked });
    }

    private handleMouseEnter = (event: React.MouseEvent<any>) => {
        this.setState({ isHovering: true });
    }

    private handleMouseLeave = (event: React.MouseEvent<any>) => {
        this.setState({ isHovering: false });
    }

    private handleWarning = (msg: string) => {
        printWarning(`${msg}. Prop name: ${this.props.name}`);
    }
}

const EDITOR_MODE_NAMES: { [key: string]: string | undefined } = {
    text: Utilities.humanizeLabel(FRENDS.Localizations.EnvironmentVariables_DatatypeText),
    xml: "XML",
    json: "JSON",
    sql: "SQL"
};

const OTHER_MODE_NAMES: { [key: string]: string | undefined } = {
    integer: Utilities.humanizeLabel(FRENDS.Localizations.EnvironmentVariables_DatatypeNumber),
    select: FRENDS.Localizations.ParamaterMode_Enum,
    csharp: FRENDS.Localizations.Expression,
    toggle: Utilities.humanizeLabel(FRENDS.Localizations.EnvironmentVariables_DatatypeYesNo),
    array: FRENDS.Localizations.Array
};

/** Valid values for ParameterEditorMode.mode */
export enum ParameterEditorMode {
    integer = 1,
    toggle,
    select,
    text, // Used for normal HTML input fields 
    handlebars, // Used for Tasks, BPMN elements etc. 
    csharp,
    xml,
    yaml,
    json,
    sql,
    array,
    plaintext, // Used only for Trigger fields
    markdown
}

interface ParameterInputOwnProps extends React.ClassAttributes<ParameterInput> {
    elementId: string | undefined;
    name: string | undefined;
    label: string | undefined;
    value: ParameterEditorValue;
    parameterSchema?: IParameterSchema | null;
    requiredPattern?: string;
    isValid?: boolean;
    min?: number;
    max?: number;
    isForCompletion?: boolean;
}

interface ParameterInputStateProps {
    errors: ValidationError[];
    disabled?: boolean;
    panelWidth?: number;
}

interface ParameterInputElementDispatchProps {
    onChange: (newValue: ParameterEditorValue, isValid: boolean) => void;
    onChangeParameterInputMode?: (newMode: string) => void;
    onToggleWordWrap?: () => void;
}

type ParameterInputProps = ParameterInputOwnProps & ParameterInputStateProps & ParameterInputElementDispatchProps;

interface ParameterInputElementOwnProps extends ParameterInputOwnProps {
    disabled?: boolean;
    onWarning: (msg: string) => void;
    panelWidth: number | undefined;
    isUnmasked: boolean;
    onUnmask: () => void;
}

type ParameterInputElementProps = ParameterInputElementOwnProps & ParameterInputElementDispatchProps;

export interface ParameterInputState {
    isEditorInitialized?: boolean;
    isHovering?: boolean;
    isUnmasked: boolean;
}

function mapStateToProps(state: State, ownProps: ParameterInputOwnProps): ParameterInputStateProps {
    let errors: ValidationError[] = [];
    if (state.validation.validationDictionary[ownProps.elementId as string]) {
        const elementErrors = state.validation.validationDictionary[ownProps.elementId as string];
        errors = elementErrors.filter((e) => e.parameterKey === ownProps.name);
    }
    return {
        errors: errors,
        disabled: state.uiState.disableEdit,
        panelWidth: state.uiState.panelSettings?.elementPanel.width
    };
}

function mapDispatchToProps(dispatch: DispatchType<State>, ownProps: ParameterInputOwnProps): ParameterInputElementDispatchProps {
    return {
        onToggleWordWrap: () => dispatch(toggleWordWrap(ownProps.elementId, ownProps.name)),
        onChangeParameterInputMode: (newMode: string) =>
            dispatch(changeParameterInputMode(ownProps.elementId, ownProps.name, ownProps.parameterSchema, ownProps.value, newMode)),
        onChange: (newValue: ParameterEditorValue, isValid: boolean) =>
            dispatch(updateParameter(ownProps.elementId, ownProps.name, newValue))
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(ParameterInput);
